import type {
  GetBrandDto,
  StockMachineAttachmentDataDto,
  StockMachineAttachmentDto,
  StockMachineDataDto,
  StockMachineDto
} from '@api/machines'
import { MachinesApiService } from '@api/machines'
import {
  ATTACHMENT_SUB_PATH,
  EQUIPMENT_SUB_PATH,
  handleCatchError,
  StockTypes
} from '@common/utils'
import {
  MolsonBox,
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader,
  MolsonToggle
} from '@components/reusable'
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { SelectBrandStep } from '../select-brand-step/SelectBrandStep'
import { SelectMachineStep } from '../select-machine-step/SelectMachineStep'
import { SelectStockType } from '../select-stock-type/SelectStockType'
import { StockItemHeader } from '../stock-item-header/StockItemHeader'

export const MACHINE_TYPE = {
  ATTACHMENT: 'attachment',
  EQUIPMENT: 'equipment',
  NO_TYPE: ''
} as const

export type MachineTypeValues = (typeof MACHINE_TYPE)[keyof typeof MACHINE_TYPE]

export type SelectMachineDialogProps = {
  brandsByEqType?: GetBrandDto[]
  brandsForMachine?: GetBrandDto[]
  fetchBrandsByEqType: (eqType: 'attachment' | 'machine') => Promise<void>
  getAttachmentsStock?: (
    machineAttachmentId: number,
    stockStatus: StockTypes
  ) => Promise<StockMachineAttachmentDataDto[] | undefined>
  getMachinesStock?: (
    machineId: number,
    stockStatus: StockTypes
  ) => Promise<StockMachineDataDto[] | undefined>
  getStockAttachmentsByBrandId: (
    brandId: number,
    tonnage?: number
  ) => Promise<StockMachineAttachmentDto[] | undefined>
  getStockMachinesByBrandId: (
    brandId: number
  ) => Promise<StockMachineDto[] | undefined>
  machineId?: number
  machineType: MachineTypeValues
  onClose: () => void
  open: boolean
  setBrandsByEqType: Dispatch<SetStateAction<GetBrandDto[]>>
  tonnage?: number
  translationPrefix?: string
}

export enum EQUIPMENT_STEPS {
  SELECT_BRAND = 'selectBrand',
  SELECT_EQ = 'selectEquipment',
  SELECT_ITEMS = 'selectItems'
}

export const SelectMachineDialog = ({
  brandsByEqType,
  brandsForMachine,
  fetchBrandsByEqType,
  getAttachmentsStock,
  getMachinesStock,
  getStockAttachmentsByBrandId,
  getStockMachinesByBrandId,
  machineId,
  machineType,
  onClose,
  open,
  setBrandsByEqType,
  tonnage,
  translationPrefix
}: SelectMachineDialogProps) => {
  const navigate = useNavigate()

  const [activeStep, setActiveStep] = useState<EQUIPMENT_STEPS>(
    EQUIPMENT_STEPS.SELECT_BRAND
  )

  const [selectedBrand, setSelectedBrand] = useState<GetBrandDto | null>()
  const [selectedMachine, setSelectedMachine] = useState<
    StockMachineAttachmentDto | StockMachineDto | null
  >(null)

  const [selectedStock, setSelectedStock] = useState<
    StockMachineAttachmentDataDto | StockMachineDataDto | null
  >(null)

  const [nextDisabled, setNextDisabled] = useState<boolean>(false)

  const { equipmentId, quoteId: quoteIdParam, specId } = useParams()
  const [inStockChecked, setInStockChecked] = useState(false)

  const quoteId = quoteIdParam ?? specId

  const onRowClick = useCallback(
    (machine: StockMachineAttachmentDto | StockMachineDto) =>
      setSelectedMachine(machine),
    []
  )

  const onSelectStock = useCallback(
    (equipment: StockMachineAttachmentDataDto | StockMachineDataDto) =>
      setSelectedStock(equipment),
    []
  )

  const { t } = useTranslation('', {
    keyPrefix: translationPrefix
  })

  const shouldAddAttachmentToMachine = machineId !== undefined

  const handleNextMachineStepSelected = async () => {
    if (!quoteId) {
      return null
    }

    if (shouldAddAttachmentToMachine) {
      await addStockAttachmentWithinMachine()
      return
    }

    if (machineType === MACHINE_TYPE.EQUIPMENT) {
      try {
        const response = await MachinesApiService.quotationAddMachine(
          quoteId,
          selectedMachine?.id as number
        )

        const quotationItemId = response.quotationItemId

        navigate(`${EQUIPMENT_SUB_PATH}/${quotationItemId}`)
      } catch (error) {
        handleCatchError(error)
      }
    } else {
      try {
        const response = await MachinesApiService.quotationAddMachineAttachment(
          quoteId,
          selectedMachine?.id as number
        )

        const quotationItemId = response.quotationItemId

        navigate(`${ATTACHMENT_SUB_PATH}/${quotationItemId}`)
      } catch (error) {
        handleCatchError(error)
      }
    }
  }

  const addStockAttachmentWithinMachine = async () => {
    if (!quoteId) {
      return
    }

    try {
      const addedAttachment =
        selectedMachine?.stockType === StockTypes.FACTORY_ORDER
          ? await MachinesApiService.quotationMachineAddMachineAttachment(
              quoteId,
              equipmentId as string,
              selectedMachine?.id as number
            )
          : await MachinesApiService.quotationMachineAddMachineAttachmentStock(
              quoteId,
              equipmentId as string,
              selectedStock?.stockId as number
            )

      navigate(`${ATTACHMENT_SUB_PATH}/${addedAttachment?.quotationItemId}`)
    } catch (error) {
      handleCatchError(error)
    }
  }

  const onAddStockItemToQuotation = async () => {
    if (!(quoteId && selectedStock?.stockId)) {
      return
    }

    if (shouldAddAttachmentToMachine) {
      addStockAttachmentWithinMachine()
    } else {
      try {
        const quotationItemId = await MachinesApiService.quotationAddStockItem(
          quoteId,
          selectedStock?.stockId
        )

        const subPath =
          machineType === MACHINE_TYPE.EQUIPMENT
            ? EQUIPMENT_SUB_PATH
            : ATTACHMENT_SUB_PATH

        navigate(`${subPath}/${quotationItemId}`)
      } catch (error) {
        handleCatchError(error)
      }
    }
  }

  const onClickNext = async () => {
    setNextDisabled(true)
    switch (activeStep) {
      case EQUIPMENT_STEPS.SELECT_BRAND:
        setActiveStep(EQUIPMENT_STEPS.SELECT_EQ)
        break

      case EQUIPMENT_STEPS.SELECT_EQ:
        if (selectedMachine?.stockType === StockTypes.FACTORY_ORDER) {
          await handleNextMachineStepSelected()
        } else {
          setActiveStep(EQUIPMENT_STEPS.SELECT_ITEMS)
        }
        break

      case EQUIPMENT_STEPS.SELECT_ITEMS:
        await onAddStockItemToQuotation()
        break
    }
    setNextDisabled(false)
  }

  const onClickBack = () => {
    switch (activeStep) {
      case EQUIPMENT_STEPS.SELECT_ITEMS: {
        setActiveStep(EQUIPMENT_STEPS.SELECT_EQ)
        setSelectedMachine(null)
        setSelectedStock(null)
        break
      }

      case EQUIPMENT_STEPS.SELECT_EQ: {
        setActiveStep(EQUIPMENT_STEPS.SELECT_BRAND)
        setSelectedBrand(null)
        setInStockChecked(false)

        break
      }

      default:
        onClose()
        break
    }
  }

  const isNextButtonDisabled = () => {
    if (activeStep === EQUIPMENT_STEPS.SELECT_BRAND && selectedBrand?.id) {
      return false
    }

    if (activeStep === EQUIPMENT_STEPS.SELECT_EQ && selectedMachine) {
      return false
    }

    if (activeStep === EQUIPMENT_STEPS.SELECT_ITEMS && selectedStock) {
      return false
    }

    return true
  }

  return (
    <MolsonDialog fullWidth={true} maxWidth="lg" onClose={onClose} open={open}>
      <MolsonDialogHeader
        title={
          activeStep === EQUIPMENT_STEPS.SELECT_BRAND
            ? t('brands')
            : (selectedBrand?.name as string)
        }
        handleCloseDialog={onClose}
      />
      <MolsonBox pb={2}>
        {activeStep === EQUIPMENT_STEPS.SELECT_EQ && (
          <MolsonBox
            alignItems="center"
            display="flex"
            justifyContent="end"
            mt={2}
          >
            <MolsonToggle
              sx={{
                '.MuiTypography-root': {
                  fontWeight: 700
                }
              }}
              checked={inStockChecked}
              label={t('inStockLabel') ?? ''}
              onChange={() => setInStockChecked(checked => !checked)}
            />
          </MolsonBox>
        )}

        {activeStep === EQUIPMENT_STEPS.SELECT_ITEMS && (
          <StockItemHeader
            brand={selectedBrand?.name}
            equipment={selectedMachine}
          />
        )}
      </MolsonBox>

      <MolsonDialogContent
        sx={{
          minHeight: 160,
          padding:
            activeStep === EQUIPMENT_STEPS.SELECT_BRAND
              ? '0px 12px 24px 12px'
              : '0px 0px 24px 0px'
        }}
      >
        {activeStep === EQUIPMENT_STEPS.SELECT_BRAND && (
          <SelectBrandStep
            brandsByEqType={brandsByEqType}
            brandsForMachine={brandsForMachine}
            fetchBrandsByEqType={fetchBrandsByEqType}
            machineType={machineType === 'equipment' ? 'machine' : 'attachment'}
            selectedBrand={selectedBrand}
            setBrandsByEqType={setBrandsByEqType}
            setSelectedBrand={setSelectedBrand}
            translationPrefix={translationPrefix}
          />
        )}

        {activeStep === EQUIPMENT_STEPS.SELECT_EQ && (
          <SelectMachineStep
            getStockAttachmentsByBrandId={getStockAttachmentsByBrandId}
            getStockMachinesByBrandId={getStockMachinesByBrandId}
            inStockChecked={inStockChecked}
            onRowClick={onRowClick}
            selectedBrand={selectedBrand}
            tonnage={tonnage}
            translationPrefix={translationPrefix}
            type={machineType}
          />
        )}

        {activeStep === EQUIPMENT_STEPS.SELECT_ITEMS && (
          <SelectStockType
            equipment={selectedMachine}
            getAttachmentsStock={getAttachmentsStock}
            getMachinesStock={getMachinesStock}
            onRowClick={onSelectStock}
            type={machineType}
          />
        )}
      </MolsonDialogContent>
      <MolsonDialogActions>
        <MolsonButton
          color="secondary"
          onClick={onClickBack}
          variant="outlined"
        >
          {activeStep === EQUIPMENT_STEPS.SELECT_ITEMS ||
          activeStep === EQUIPMENT_STEPS.SELECT_EQ
            ? t('backButton')
            : t('cancelButton')}
        </MolsonButton>
        <MolsonButton
          disabled={isNextButtonDisabled() || nextDisabled}
          onClick={onClickNext}
          variant="contained"
        >
          {t('nextButton')}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
